import { useState, useMemo } from "react";
import { useHistory } from "react-router-dom";
import {
  Flex,
  Card,
  TextInput,
  Text,
  RadioGroup,
  RadioButton,
  Button,
  Tag,
  QueryLoader,
  ErrorMessage,
} from "@jsq/ds/core";
import { useFormContext } from "@jsq/ds/forms";
import { Icon } from "@jsq/ds/icons";
import { Colors } from "@jsq/ds/styles";
import { ProfileRelationshipEnum } from "~/graphql/supergraphEnums";
import { useGetPicrProfilesForBeneficiaryQuery } from "~/graphql/supergraphHooks";
import { capitalizeString } from "~/pages/Billing/utils";
import { ACCOUNT_TYPE_TO_VIEWABLE_ACCOUNT } from "~/pages/Compliance/CaseDetailsDrawer/Profiles/common/EntityFields/constants";
import { getProfileName } from "~/pages/Compliance/CaseDetailsDrawer/Profiles/utils";
import { formatShortFormDate } from "~/utils/datetime";
import { usePaymentInstructions } from "../../PaymentInstructionsContext";
import {
  BeneficiaryProfileSearchItem,
  LinkedPositionsInfo,
  PaymentInstructionFormValues,
} from "../../types";
import { filterFatcaCrsProfiles, isFatcaCrsProfile, normalizeForMatch } from "../../utils";
import ProfilePositionsModal from "./ProfilePositionsModal";
import {
  inputField,
  searchContainer,
  radioButtonItem,
  createNewButton,
  recentTag,
  profileName,
} from "./styles_vanilla.css";

type BeneficiaryProfileSearchProps = {
  accountHolderName: string;
};

const getViewableAccountType = (accountType: string | null | undefined): string => {
  if (accountType && ACCOUNT_TYPE_TO_VIEWABLE_ACCOUNT[accountType]) {
    return ACCOUNT_TYPE_TO_VIEWABLE_ACCOUNT[accountType];
  }
  return capitalizeString(accountType);
};

const BeneficiaryProfileSearch = ({ accountHolderName }: BeneficiaryProfileSearchProps) => {
  const history = useHistory();
  const { values, errors, submitCount } = useFormContext<PaymentInstructionFormValues>();

  const [selectedProfile, setSelectedProfile] = useState<string>("");
  const [searchQuery, setSearchQuery] = useState<string>(accountHolderName);
  const [showPositionsModal, setShowPositionsModal] = useState<boolean>(false);
  const [selectedProfileForPositions, setSelectedProfileForPositions] =
    useState<BeneficiaryProfileSearchItem | null>(null);
  const [linkedPositions, setLinkedPositions] = useState<LinkedPositionsInfo[]>([]);

  const { profiles, caseId } = usePaymentInstructions();

  const beneficiaryResults = useGetPicrProfilesForBeneficiaryQuery({
    variables: {
      caseId: caseId,
    },
    fetchPolicy: "network-only",
  });

  /**
   * Build the list of available profiles from PICR position data.
   *
   * This handles two key scenarios:
   * 1. A profile may appear in multiple linkedOnboardingCases - we dedupe by profileId
   * 2. When deduping, we merge all compliance cases from each occurrence
   *
   * The role is determined by checking the profile's parentRelationships
   * (Owner, Controller, or fallback to Investing Account if it's the root profile).
   */
  const availableProfiles = useMemo(() => {
    const positions = beneficiaryResults.data?.picrPositionDetailsByCaseId ?? [];
    const profileMap = new Map<number, BeneficiaryProfileSearchItem>();
    const seenCaseIds = new Map<number, Set<number>>(); // Track which cases we've added per profile

    const getRole = (
      relationships: ReadonlyArray<{ relationshipType?: string | null } | null>,
    ): string => {
      const hasOwner = relationships.some(
        (r) => r?.relationshipType === ProfileRelationshipEnum.Owner,
      );
      if (hasOwner) return "Owner";

      const hasController = relationships.some(
        (r) => r?.relationshipType === ProfileRelationshipEnum.Controller,
      );
      if (hasController) return "Controller";

      return "--";
    };

    // Process each position's linked onboarding case
    for (const position of positions) {
      const linkedCase = position?.linkedOnboardingCase;
      if (!linkedCase) continue;

      // Get all profiles: investing profile + child profiles
      const investingProfile =
        linkedCase.investingProfile?.latestVersion ?? linkedCase.investingProfile;
      const childProfiles = linkedCase.profiles.map((p) => p?.latestVersion ?? p);
      const allProfiles = [...childProfiles, investingProfile].filter(Boolean);

      for (const profile of allProfiles) {
        if (!profile) continue;

        // Filter out FATCA/CRS profiles
        if (isFatcaCrsProfile(profile)) continue;

        const isInvestingAccount =
          profile.id === investingProfile?.id || profile.id === linkedCase.investingProfile?.id;

        // Build linked positions from this profile's position history
        const positionsLinkedToProfile: LinkedPositionsInfo[] = [];

        for (const caseItem of profile.positions ?? []) {
          const complianceCase = caseItem?.onboardingCase;
          if (!complianceCase) continue;

          // Skip if we've already added this case for this profile (prevents duplicates)
          const seenSet = seenCaseIds.get(profile.id) ?? new Set<number>();
          if (seenSet.has(complianceCase.id)) continue;
          seenSet.add(complianceCase.id);
          seenCaseIds.set(profile.id, seenSet);

          // Determine role: Investing Account if this is the case's root, otherwise check relationships
          const role =
            profile.id === complianceCase.investingProfile?.id
              ? "Investing Account"
              : getRole(profile.parentRelationships);

          positionsLinkedToProfile.push({
            id: complianceCase.id,
            createdAt: formatShortFormDate(complianceCase.createdAt),
            investingAccountName: getProfileName(complianceCase.investingProfile),
            entityName: complianceCase.externalEntity?.name ?? "--",
            arenaName: complianceCase.externalArena?.name ?? "--",
            program: complianceCase.program.name,
            role: isInvestingAccount ? "Investing Account" : role,
          });
        }

        // Either merge into existing profile entry or create new one
        const existing = profileMap.get(profile.id);
        if (existing) {
          // Merge: append new positions
          existing.complianceCases = [
            ...(existing.complianceCases ?? []),
            ...positionsLinkedToProfile,
          ];
        } else {
          // Create new entry
          profileMap.set(profile.id, {
            profileId: profile.id,
            profileName: getProfileName(profile),
            accountType: getViewableAccountType(profile.accountType),
            lastUsed: profile.lastUsedAt,
            positionCount: profile.positionCount,
            complianceCases: positionsLinkedToProfile,
          });
        }
      }
    }

    // Add any newly created profiles (from context) that aren't in the map yet
    // Filter out FATCA/CRS profiles
    const filteredContextProfiles = filterFatcaCrsProfiles(profiles);
    for (const profile of filteredContextProfiles) {
      if (profileMap.has(profile.id)) continue;

      profileMap.set(profile.id, {
        profileId: profile.id,
        profileName: getProfileName(profile),
        accountType: getViewableAccountType(profile.accountType),
        complianceCases: [],
      });
    }

    return Array.from(profileMap.values());
  }, [beneficiaryResults.data?.picrPositionDetailsByCaseId, profiles]);

  const filteredProfiles = useMemo((): BeneficiaryProfileSearchItem[] => {
    const query = normalizeForMatch(searchQuery);
    if (!query) return [];

    return availableProfiles
      .filter((profile) => normalizeForMatch(profile.profileName).startsWith(query))
      .sort((a, b) => {
        // Sort by lastUsed (ISO string) descending (most recent first)
        if (!a.lastUsed && !b.lastUsed) return 0;
        if (!a.lastUsed) return 1;
        if (!b.lastUsed) return -1;
        return b.lastUsed.localeCompare(a.lastUsed);
      });
  }, [availableProfiles, searchQuery]);

  const handlePositionClick = (profile: BeneficiaryProfileSearchItem) => {
    const positions =
      filteredProfiles.find((p) => profile.profileId == p.profileId)?.complianceCases || [];

    setLinkedPositions(positions);
    setSelectedProfileForPositions(profile);
    setShowPositionsModal(true);
  };

  const createNewProfile = () => {
    history.push(`${history.location.pathname}/create/profile`);
  };

  const handleEditProfile = (profileId: number) => {
    history.push(`${history.location.pathname}/edit/profile/${profileId}`);
  };

  return (
    <QueryLoader result={beneficiaryResults}>
      <Card className={searchContainer}>
        <Text variant="h3">Add payment beneficiary</Text>
        <TextInput
          className={inputField}
          label="Search existing profile or add a new one"
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          isFullWidth
        />
        {submitCount > 0 && errors.accountHolderId && (
          <ErrorMessage>{errors.accountHolderId}</ErrorMessage>
        )}
        {filteredProfiles.length > 0 ? (
          <RadioGroup
            name="selectedProfile"
            onChange={(e) => {
              const profileId = e.target.value;
              setSelectedProfile(profileId);
              values.accountHolderId = Number(profileId);
            }}
          >
            <Flex flexDirection="column">
              {filteredProfiles.map((profile, index) => (
                <RadioButton
                  key={profile.profileId}
                  value={profile.profileId}
                  checked={selectedProfile === String(profile.profileId)}
                  name="selectedProfile"
                  className={radioButtonItem}
                >
                  <Flex
                    className={profileName}
                    justifyContent="space-between"
                    alignItems="flex-start"
                  >
                    <Flex flexDirection="column">
                      <Text variant="strong-md">{profile.profileName}</Text>
                      <Flex alignItems="center" gap={4}>
                        <Text variant="regular-sm">
                          {profile.accountType}
                          {profile.lastUsed &&
                            ` | Last used ${formatShortFormDate(profile.lastUsed)}`}
                        </Text>
                        {(profile.positionCount ?? 0) > 0 && (
                          <>
                            {`| `}
                            <Button
                              displayAsLink
                              variant="link"
                              onClick={() => {
                                handlePositionClick(profile);
                              }}
                            >
                              {`${profile.positionCount}`}
                            </Button>
                            <Text variant="regular-sm">
                              {(profile.positionCount ?? 0) > 1 ? `Positions` : `Position`}
                            </Text>
                          </>
                        )}
                      </Flex>
                    </Flex>
                    <Flex className={recentTag}>
                      {index === 0 && profile.lastUsed && (
                        <Tag variant="neutral">Recently used</Tag>
                      )}
                    </Flex>
                    <Flex>
                      {!profile.positionCount && <Tag variant="neutral">Recently created</Tag>}
                      {!profile.positionCount && (
                        <Icon
                          data-testid="edit-icon"
                          type="edit"
                          color={Colors.Primary600}
                          size="x-large"
                          onClick={() => handleEditProfile(profile.profileId)}
                        />
                      )}
                    </Flex>
                  </Flex>
                </RadioButton>
              ))}
            </Flex>
          </RadioGroup>
        ) : (
          searchQuery.trim() && (
            <Button
              className={createNewButton}
              icon="plus"
              variant="link"
              onClick={createNewProfile}
            >
              {`Create a new profile for ${searchQuery}`}
            </Button>
          )
        )}
      </Card>
      {showPositionsModal && (
        <ProfilePositionsModal
          profileName={selectedProfileForPositions?.profileName ?? ""}
          positionCount={selectedProfileForPositions?.positionCount ?? 0}
          linkedPositions={linkedPositions}
          closeModal={() => setShowPositionsModal(false)}
        />
      )}
    </QueryLoader>
  );
};
export default BeneficiaryProfileSearch;
